import { modifier } from 'constant'
import { useEvent, useOutsideClick } from 'hooks'
import M from 'materialize-css'
import { cn } from 'utils'

const handleSpecialOptions = (elem: HTMLSelectElement) => {
	const select = elem.M_FormSelect
	if (!select) {
		return
	}

	const options = select.dropdownOptions.querySelectorAll('li:not(.optgroup)')
	const noOptionsValue = elem.getAttribute('data-no-options')

	options.forEach((option: HTMLLIElement) => {
		option.addEventListener('click', () => {
			if (!option.textContent) {
				return
			}

			const optionValue = option.textContent.trim()

			if (optionValue === noOptionsValue && option.classList.contains('selected')) {
				options.forEach((opt: HTMLLIElement) => {
					if (opt.textContent && opt.textContent.trim() !== noOptionsValue) {
						opt.classList.remove('selected')
						opt.classList.add('disabled')
					}
				})
			} else if (optionValue === noOptionsValue && !option.classList.contains('selected')) {
				options.forEach((opt: HTMLLIElement) => {
					opt.classList.remove('disabled')
				})
			}
			select.dropdown.recalculateDimensions()
		})
	})
}

const Select = () => {
	const init = () => {
		const targets = document.querySelectorAll('select')

		for (let i = 0, { length } = targets; i < length; i++) {
			const item = targets[i]
			const { parentNode } = item
			const container = (item.closest('.modal, .modal-content') as HTMLElement) ?? document.body

			const select = M.FormSelect.init(item, {
				dropdownOptions: {
					container,
				},
			})

			if (select.input) {
				select.input.placeholder = item.getAttribute('placeholder') || ''
				select.input.ariaLabel = item.getAttribute('name') || ''
			}

			const scope = select.dropdownOptions
			const click = useEvent(select.input, 'click')
			if (parentNode) {
				const firstElement = parentNode.querySelector('.select-wrapper')
				if (firstElement) {
					const [lastPart] = select.el.id.split('-').slice(-1)
					firstElement.setAttribute('data-testing', `${lastPart}-select`)
					firstElement.id = `${lastPart}-select`
				}

				const svg = parentNode.querySelector('svg')
				if (svg) {
					svg.setAttribute('aria-hidden', 'true')
				}
			}
			if (select.dropdownOptions) {
				const options = select.dropdownOptions.querySelectorAll('li:not(.optgroup)')
				options.forEach((option: Element) => {
					if (option.firstElementChild) {
						const span = option.firstElementChild.querySelector('span')
						if (span && span.textContent !== null) {
							span.setAttribute('data-testing', `${span.textContent}-option`)
							span.id = `${option.id}-option`
						}
					}
				})
			}
			click.register(() => {
				if (!cn.hasClass(scope, modifier.open)) {
					cn.addClass(scope, modifier.open)
				}
			})
			useOutsideClick(select.input, () => {
				cn.removeClass(scope, modifier.open)
			})
			addSearch(item)
			handleSpecialOptions(item)
		}
	}

	const addSearch = (elem: HTMLSelectElement) => {
		const select = elem.M_FormSelect
		const isNotSearchable = elem.getAttribute('searchable-disabled')

		if (!select || isNotSearchable) {
			return
		}

		const options = select.dropdownOptions.querySelectorAll('li:not(.optgroup)')

		if (options.length <= 10) {
			return
		}
		const searchBox = document.createElement('div')
		searchBox.style.padding = '0 16px'
		searchBox.innerHTML = `
            <input type="search" name="${elem.name}-search">
            </input>`
		select.dropdownOptions.prepend(searchBox)

		// Function to filter dropdown options
		function filterOptions(event: Event) {
			if (!event.target) {
				return
			}
			const searchText = event.target.value.toLowerCase()
			options.forEach((option: HTMLLIElement) => {
				if (option.textContent === null) {
					return
				}
				const value = option.textContent.toLowerCase()
				option.style.display = value.indexOf(searchText) === -1 ? 'none' : 'block'
			})

			select.dropdown.recalculateDimensions()
		}

		// Function to give keyboard focus to the search input field
		select.dropdown.options.autoFocus = false

		if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
			select.input.addEventListener('click', () => {
				if (!searchBox.firstElementChild) {
					return
				}
				searchBox.firstElementChild.focus({ preventScroll: true })
			})
			options.forEach((option: HTMLLIElement) => {
				option.addEventListener('click', () => {
					if (!searchBox.firstElementChild) {
						return
					}
					searchBox.firstElementChild.focus({ preventScroll: true })
				})
			})
		}
		searchBox.addEventListener('keyup', filterOptions)
	}

	return {
		init,
	}
}

export default Select
